import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  RefreshCw,
  Send,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  getUsdcBalance,
  transferUsdc,
  TurnkeyAdminCredentials,
} from '@/services/turnkeyWallet';
import { isValidAddressForChain } from '@/utils/chains';

import { OPS_CHAIN } from './shared';

// ─── Types ────────────────────────────────────────────────────────────────────

type OperationStatus = {
  type: 'success' | 'error' | null;
  message: string;
  txHash?: string;
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const WalletOpsPage = () => {
  const [credentials, setCredentials] =
    useState<TurnkeyAdminCredentials | null>(null);
  const [usdcBalance, setUsdcBalance] = useState<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    if (!credentials?.walletAddress) return;
    refreshBalance(credentials.walletAddress);
  }, [credentials?.walletAddress]);

  const refreshBalance = async (address: string) => {
    setBalanceLoading(true);
    try {
      const bal = await getUsdcBalance(address);
      setUsdcBalance(bal);
    } catch {
      setUsdcBalance(null);
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleAuthenticate = (creds: TurnkeyAdminCredentials) => {
    setCredentials(creds);
  };

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Wallet Operations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage master wallet funds directly via Turnkey. Transactions are
          signed in the browser using your API credentials.
        </p>
      </div>

      {!credentials ? (
        <CredentialGate onAuthenticate={handleAuthenticate} />
      ) : (
        <AuthenticatedOps
          credentials={credentials}
          usdcBalance={usdcBalance}
          balanceLoading={balanceLoading}
          onRefreshBalance={() => refreshBalance(credentials.walletAddress)}
          onLock={() => {
            setCredentials(null);
            setUsdcBalance(null);
          }}
        />
      )}
    </div>
  );
};

// ─── Credential Gate ──────────────────────────────────────────────────────────

const CredentialGate = ({
  onAuthenticate,
}: {
  onAuthenticate: (creds: TurnkeyAdminCredentials) => void;
}) => {
  const [form, setForm] = useState({
    organizationId: '',
    walletAddress: '',
    apiPublicKey: '',
    apiPrivateKey: '',
  });
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !form.organizationId ||
      !form.walletAddress ||
      !form.apiPublicKey ||
      !form.apiPrivateKey
    ) {
      setError('All fields are required.');
      return;
    }
    // Checked before unlocking, for the same reason as the transfer form: a malformed
    // address here surfaces as a confusing failure several steps later.
    if (!isValidAddressForChain(OPS_CHAIN, form.walletAddress)) {
      setError('Wallet address is not a valid address for this chain.');
      return;
    }
    setError('');
    onAuthenticate(form);
  };

  return (
    <Card className="max-w-md mx-auto mt-10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Turnkey Credentials</CardTitle>
        </div>
        <CardDescription>
          All operations are signed and broadcast directly from your browser.
          Nothing is sent to the backend — credentials are never stored.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="org-id">Organization ID</Label>
            <Input
              id="org-id"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={form.organizationId}
              onChange={(e) => handleChange('organizationId', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="wallet-address">Master Wallet Address</Label>
            <Input
              id="wallet-address"
              placeholder="0x..."
              value={form.walletAddress}
              onChange={(e) => handleChange('walletAddress', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pub-key">API Public Key</Label>
            <Input
              id="pub-key"
              placeholder="Ed25519 public key (hex)"
              value={form.apiPublicKey}
              onChange={(e) => handleChange('apiPublicKey', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="priv-key">API Private Key</Label>
            <div className="relative">
              <Input
                id="priv-key"
                type={showPrivateKey ? 'text' : 'password'}
                placeholder="Ed25519 private key (hex)"
                value={form.apiPrivateKey}
                onChange={(e) => handleChange('apiPrivateKey', e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPrivateKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={
                  showPrivateKey ? 'Hide private key' : 'Show private key'
                }
              >
                {showPrivateKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-destructive flex items-center gap-1.5">
              <AlertCircle size={13} />
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={
              !form.organizationId ||
              !form.walletAddress ||
              !form.apiPublicKey ||
              !form.apiPrivateKey
            }
          >
            Unlock Wallet Operations
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ─── Authenticated Ops ────────────────────────────────────────────────────────

const AuthenticatedOps = ({
  credentials,
  usdcBalance,
  balanceLoading,
  onRefreshBalance,
  onLock,
}: {
  credentials: TurnkeyAdminCredentials;
  usdcBalance: string | null;
  balanceLoading: boolean;
  onRefreshBalance: () => void;
  onLock: () => void;
}) => (
  <div className="space-y-4">
    {/* Session bar */}
    <div className="flex items-center justify-between rounded-md border px-4 py-2.5 bg-muted/40">
      <div className="flex items-center gap-2 text-sm text-green-600">
        <CheckCircle2 size={15} />
        <span>
          Authenticated &mdash;{' '}
          <span className="font-mono text-xs text-muted-foreground">
            {credentials.organizationId.slice(0, 8)}…
          </span>
        </span>
      </div>
      <Button variant="outline" size="sm" onClick={onLock}>
        <LockKeyhole size={14} className="mr-1.5" />
        Lock
      </Button>
    </div>

    {/* Master wallet info */}
    <MasterWalletBanner
      walletAddress={credentials.walletAddress}
      usdcBalance={usdcBalance}
      balanceLoading={balanceLoading}
      onRefresh={onRefreshBalance}
    />

    {/* Operations */}
    <Tabs defaultValue="send">
      <TabsList className="w-full">
        <TabsTrigger value="send" className="flex-1">
          <Send className="mr-2 h-4 w-4" />
          Send Money
        </TabsTrigger>
        <TabsTrigger value="retrieve" className="flex-1">
          <Wallet className="mr-2 h-4 w-4" />
          Retrieve Money
        </TabsTrigger>
      </TabsList>

      <TabsContent value="send">
        <TransferForm
          credentials={credentials}
          onSuccess={onRefreshBalance}
          title="Send USDC to User"
          description="Transfer USDC from the master wallet to a user or partner wallet."
          recipientLabel="Recipient Wallet Address"
          submitLabel="Send USDC"
          submitIcon={<Send className="mr-2 h-4 w-4" />}
        />
      </TabsContent>

      <TabsContent value="retrieve">
        <TransferForm
          credentials={credentials}
          onSuccess={onRefreshBalance}
          title="Withdraw USDC"
          description="Move USDC from the master wallet to an external address (cold wallet, exchange, etc.)."
          recipientLabel="Destination Address"
          submitLabel="Withdraw USDC"
          submitIcon={<Wallet className="mr-2 h-4 w-4" />}
        />
      </TabsContent>
    </Tabs>
  </div>
);

// ─── Master Wallet Banner ─────────────────────────────────────────────────────

const MasterWalletBanner = ({
  walletAddress,
  usdcBalance,
  balanceLoading,
  onRefresh,
}: {
  walletAddress: string;
  usdcBalance: string | null;
  balanceLoading: boolean;
  onRefresh: () => void;
}) => {
  const explorerUrl = process.env.REACT_APP_POLYGON_EXPLORER_URL;

  return (
    <Card className="bg-muted/30">
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground mb-0.5">
              Master Wallet (Polygon)
            </p>
            <p className="font-mono text-sm break-all">{walletAddress}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <p className="text-xs text-muted-foreground">Balance:</p>
              {balanceLoading ? (
                <Loader2
                  size={12}
                  className="animate-spin text-muted-foreground"
                />
              ) : usdcBalance !== null ? (
                <p className="text-xs font-medium">{usdcBalance} USDC</p>
              ) : (
                <p className="text-xs text-muted-foreground">unavailable</p>
              )}
              <button
                onClick={onRefresh}
                disabled={balanceLoading}
                className="text-muted-foreground hover:text-foreground disabled:opacity-40"
                aria-label="Refresh balance"
              >
                <RefreshCw size={11} />
              </button>
            </div>
          </div>
          {explorerUrl && (
            <a
              href={`${explorerUrl}/address/${walletAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0"
            >
              <Button variant="ghost" size="sm">
                <ExternalLink size={14} className="mr-1.5" />
                Explorer
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Transfer Form ────────────────────────────────────────────────────────────

const TransferForm = ({
  credentials,
  onSuccess,
  title,
  description,
  recipientLabel,
  submitLabel,
  submitIcon,
}: {
  credentials: TurnkeyAdminCredentials;
  onSuccess: () => void;
  title: string;
  description: string;
  recipientLabel: string;
  submitLabel: string;
  submitIcon: React.ReactNode;
}) => {
  const [form, setForm] = useState({ address: '', amount: '', note: '' });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<OperationStatus>({
    type: null,
    message: '',
  });

  const handleChange = (field: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.address.trim() || !form.amount.trim()) return;

    setLoading(true);
    setStatus({ type: null, message: '' });

    try {
      const txHash = await transferUsdc(
        credentials,
        form.address.trim(),
        form.amount.trim(),
      );

      setStatus({
        type: 'success',
        message: `Successfully transferred ${form.amount} USDC to ${form.address}.`,
        txHash,
      });
      setForm({ address: '', amount: '', note: '' });
      onSuccess();
    } catch (err: any) {
      setStatus({
        type: 'error',
        message: err?.message ?? 'Transfer failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`addr-${title}`}>{recipientLabel}</Label>
            <Input
              id={`addr-${title}`}
              placeholder="0x..."
              value={form.address}
              onChange={(e) => handleChange('address', e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`amt-${title}`}>Amount</Label>
            <div className="relative">
              <Input
                id={`amt-${title}`}
                type="number"
                min="0"
                step="any"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                className="pr-16"
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                USDC
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`note-${title}`}>
              Note{' '}
              <span className="text-muted-foreground font-normal">
                (optional)
              </span>
            </Label>
            <Input
              id={`note-${title}`}
              placeholder="Internal reference"
              value={form.note}
              onChange={(e) => handleChange('note', e.target.value)}
            />
          </div>

          <OperationStatusBanner status={status} />

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !form.address || !form.amount}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing & Broadcasting...
              </>
            ) : (
              <>
                {submitIcon}
                {submitLabel}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ─── Status Banner ────────────────────────────────────────────────────────────

const OperationStatusBanner = ({ status }: { status: OperationStatus }) => {
  if (!status.type) return null;

  const isSuccess = status.type === 'success';
  return (
    <div
      className={`rounded-md px-3 py-2.5 text-sm border ${
        isSuccess
          ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-red-50 text-red-700 border-red-200'
      }`}
    >
      <div className="flex items-start gap-2">
        {isSuccess ? (
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        ) : (
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        )}
        <span>{status.message}</span>
      </div>
      {isSuccess && status.txHash && (
        <p className="mt-1.5 ml-6 font-mono text-xs text-green-600 break-all">
          Tx: {status.txHash}
        </p>
      )}
    </div>
  );
};

export default WalletOpsPage;
